import { useState, useRef, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/TextLayer.css";
import "react-pdf/dist/Page/AnnotationLayer.css";

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

export default function PDFViewer({ pdfUrl, storageUrl, contentId, onInsertToChat }) {
  const effectiveUrl = storageUrl || pdfUrl;
  const [numPages, setNumPages] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [pageWidth, setPageWidth] = useState(800);
  const [visiblePages, setVisiblePages] = useState([1, 2, 3]);

  const [selectedText, setSelectedText] = useState("");
  const [popupPos, setPopupPos] = useState(null);

  const containerRef = useRef(null);
  const navigate = useNavigate();
  const { contentId: routeContentId } = useParams();
  const actualContentId = contentId || routeContentId;

  // Redirect if contentId mismatch
  useEffect(() => {
    if (actualContentId && !window.location.pathname.includes(`/c/${actualContentId}`)) {
      navigate(`/c/${actualContentId}`);
    }
  }, [actualContentId, navigate]);

  // Resize handling
  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        setPageWidth(containerRef.current.offsetWidth - 16);
      }
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setLoading(false);
    setLoadError(false);
  };

  const onDocumentLoadError = (err) => {
    setLoadError(true);
    setLoading(false);
  };

  // Infinite scroll
  const handleScroll = () => {
    if (!containerRef.current || !numPages) return;

    const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
    if (scrollTop + clientHeight >= scrollHeight - 300) {
      setVisiblePages((prev) => {
        const lastPage = prev[prev.length - 1];
        const nextPages = [];
        for (let i = 1; i <= 3; i++) {
          if (lastPage + i <= numPages) nextPages.push(lastPage + i);
        }
        return [...prev, ...nextPages];
      });
    }
  };

  // 🔹 Global text selection handling (works for react-pdf text layers)
  useEffect(() => {
    const handleSelection = () => {
      const selection = window.getSelection();
      if (!selection || !selection.toString().trim().length) {
        setSelectedText("");
        setPopupPos(null);
        return;
      }

      // Ensure selection lives inside the PDF viewer (containerRef) and a PDF page element.
      // This prevents showing the Ask AI popup for selections outside the PDF (e.g. UI text).
      let anchorNode = selection.anchorNode;
      let focusNode = selection.focusNode;
      const containerEl = containerRef.current;
      if (!containerEl) {
        setSelectedText("");
        setPopupPos(null);
        return;
      }

      const isNodeInsideContainer = (node) => {
        if (!node) return false;
        // If a text node, use parent element
        const el = node.nodeType === 3 ? node.parentElement : node;
        if (!el) return false;
        if (!containerEl.contains(el)) return false;
        // Prefer selection that is inside a rendered PDF page element
        return !!el.closest('.react-pdf__Page') || !!el.closest('.textLayer');
      };

      if (!isNodeInsideContainer(anchorNode) || !isNodeInsideContainer(focusNode)) {
        // selection is outside the PDF area; ignore it
        setSelectedText("");
        setPopupPos(null);
        return;
      }

      const selected = selection.toString();
      setSelectedText(selected);

      try {
        const range = selection.getRangeAt(0);
        const rect = range.getBoundingClientRect();
        const containerRect = containerEl.getBoundingClientRect();

        // Compute coordinates relative to the container (account for container scroll)
        const relativeTop = rect.bottom - containerRect.top + containerEl.scrollTop + 8; // +8 for small padding offset
        const relativeLeftCenter = rect.left - containerRect.left + containerEl.scrollLeft + rect.width / 2;

        // Clamp popup within container bounds (leave 8px padding)
        const minLeft = 8;
        const maxLeft = Math.max(8, containerEl.clientWidth - 8);
        const clampedLeft = Math.min(Math.max(relativeLeftCenter, minLeft), maxLeft);

        setPopupPos({
          top: Math.round(relativeTop),
          left: Math.round(clampedLeft)
        });
      } catch (err) {
        // Selection range may be invalid (e.g., across iframes) — hide popup
        setPopupPos(null);
      }
    };

    window.addEventListener("mouseup", handleSelection);
    window.addEventListener("touchend", handleSelection); // mobile support

    return () => {
      window.removeEventListener("mouseup", handleSelection);
      window.removeEventListener("touchend", handleSelection);
    };
  }, []);

  const handleAskAI = () => {
    if (!selectedText) return;
    // If parent provided a handler, call it to insert text into chat input and switch to chat
    if (typeof onInsertToChat === "function") {
      try {
        onInsertToChat(selectedText);
      } catch (e) {
        // fallback to simple alert in case handler errors
        alert("Ask AI with: " + selectedText);
      }
    } else {
      // fallback behavior
      alert("Ask AI with: " + selectedText);
    }
    // Clear selection and popup after inserting
    setSelectedText("");
    setPopupPos(null);
  };

  return (
    <div
      ref={containerRef}
      onScroll={handleScroll}
      className="relative flex flex-col border rounded-lg bg-white"
      style={{ aspectRatio: "16 / 12", overflowY: "auto", padding: "8px" }}
    >
      {loadError ? (
        <div className="text-center p-4">
          <p className="text-red-600 font-medium mb-2">Failed to load document.</p>
          <p className="text-sm text-gray-500">
            Make sure it's a valid and publicly accessible PDF.
          </p>
        </div>
      ) : (
        <Document
          file={effectiveUrl}
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadError={onDocumentLoadError}
        >
          {visiblePages.map((pageNumber) => (
            <Page
              key={`page_${pageNumber}`}
              pageNumber={pageNumber}
              width={pageWidth}
              renderTextLayer={true} // enable text selection
              renderAnnotationLayer={false}
              loading={
                <div className="flex items-center justify-center p-4 h-64">
                  <div className="animate-spin h-6 w-6 border-2 border-blue-500 border-t-transparent rounded-full" />
                  <span className="ml-3 text-sm text-gray-600">
                    Rendering page {pageNumber}...
                  </span>
                </div>
              }
              className="mb-4 mx-auto shadow-sm"
            />
          ))}
        </Document>
      )}

      {loading && !loadError && (
        <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-80 z-10">
          <div className="animate-spin h-6 w-6 border-2 border-blue-500 border-t-transparent rounded-full" />
          <span className="ml-3 text-sm text-gray-600">Loading document...</span>
        </div>
      )}

      {/* 🔹 Ask AI popup */}
      {selectedText && popupPos && (
        <div
          className="absolute z-50"
          style={{
            top: popupPos.top,
            left: popupPos.left,
            transform: "translate(-50%, 0)",
          }}
        >
          <button
            onClick={handleAskAI}
            className="px-3 py-1 bg-black text-white text-sm rounded-full shadow-md"
          >
            Ask AI
          </button>
        </div>
      )}
    </div>
  );
}
